import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useKasa } from '../../context/KasaContext';
import { apiRequest } from '../../services/api';
import DatePicker from '../../components/DatePicker';
import { GiderEkleModal } from '../../components/KasaModals';
import ExcelImportModal from '../../components/ExcelImportModal';
import UsageInfo from '../../components/UsageInfo';
import { downloadExcel, printTable } from '../../utils/exportUtils';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import ModalShell from '../../components/ModalShell';
import OnayPopup from '../../components/OnayPopup';
import { formatPara } from '../../utils/formatUtils';
import Pagination from '../../components/Pagination';
import DataTable, { Rozet } from '../../components/DataTable';
import ActionButtons, { EditButton } from '../../components/ActionButtons';

// ── Sabitler ──
const GIDER_TIPLERI = ['Hepsi', 'Gider', 'Personel Gider'];
const ODEME_TIPLER = ['Hepsi', 'KREDİ KARTI', 'NAKİT', 'BANKA HAVALE', 'ÇEK'];

function pad(n) { return String(n).padStart(2, '0'); }
function bugunStr() {
  var d = new Date();
  return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear();
}
function ayinBasiStr() {
  var d = new Date(); d.setDate(1);
  return pad(1) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear();
}
function ddmmyyyyToDate(s) {
  if (!s) return null;
  var p = s.split('.');
  if (p.length !== 3) return null;
  return new Date(parseInt(p[2]), parseInt(p[1]) - 1, parseInt(p[0]));
}

// ── Searchable Select (filtre için) ──
function SearchableSelect({ value, onChange, options }) {
  var [open, setOpen] = useState(false);
  var [search, setSearch] = useState('');
  var ref = useRef();
  useEffect(function () {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return function () { document.removeEventListener('mousedown', handler); };
  }, []);
  var filtered = options.filter(function (o) { return o.toLocaleLowerCase('tr-TR').includes(search.toLocaleLowerCase('tr-TR')); });
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={function () { setOpen(function (v) { return !v; }); setSearch(''); }}
        className="w-full flex items-center justify-between border-2 border-gray-100 rounded-2xl px-5 py-3.5 text-xs font-bold bg-white hover:border-blue-400 focus:outline-none transition-colors shadow-sm focus:ring-4 focus:ring-blue-50">
        <span className={value === 'Hepsi' || !value ? 'text-gray-400 uppercase tracking-widest text-[10px] font-black' : 'text-gray-800 uppercase tracking-widest text-[10px] font-black truncate'}>{value || 'Hepsi'}</span>
        <span className="text-gray-300 ml-2 text-[10px]">▼</span>
      </button>
      {open && (
        <div className="absolute z-50 mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl min-w-[140px] max-h-52 overflow-y-auto w-full p-2 animate-fadeIn origin-top">
          <div className="pb-2 mb-2 border-b border-gray-50">
            <input autoFocus value={search} onChange={function (e) { setSearch(e.target.value); }} placeholder="Ara..."
              className="w-full bg-gray-50 border-none rounded-xl px-3 py-2 text-xs font-bold focus:ring-2 focus:ring-blue-100 focus:outline-none uppercase tracking-widest" />
          </div>
          {filtered.map(function (o) {
            return (
              <div key={o} onClick={function () { onChange(o); setOpen(false); }}
                className={'px-3 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl cursor-pointer transition-colors ' + (value === o ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-50 text-gray-600')}>
                {o}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Sabit Gider Modal ──
function SabitGiderModal({ kayit, giderGruplari, personeller, odemeTipleri, bankalar, paraBirimleri, onKaydet, onSil, onKapat }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  var duzenleme = !!kayit?.sabitGiderId;
  const INP = (hata) => `w-full border-2 rounded-2xl px-5 py-3.5 text-xs font-bold text-gray-800 transition-all duration-200 bg-white focus:outline-none ${hata ? 'border-rose-400 bg-rose-50 focus:border-rose-500' : 'border-gray-100 focus:border-blue-400 focus:ring-4 focus:ring-blue-50'}`;

  var [tip, setTip] = useState((kayit?.giderTipi || 'gider').toLowerCase());
  var [ad, setAd] = useState(kayit?.ad || '');
  var [aciklama, setAciklama] = useState(kayit?.aciklama || '');
  var [tutar, setTutar] = useState(kayit?.birimTutar || 0);
  var [miktar, setMiktar] = useState(kayit?.miktar || 1);
  var paraBirimiListesiLocal = paraBirimleri.length ? paraBirimleri.map(p => p.tcmbKod || p.ad) : ['TL', 'USD', 'EUR'];
  var [paraBirimi, setParaBirimi] = useState(kayit?.paraBirimi || paraBirimiListesiLocal.find(p => p === 'TRY' || p === 'TL') || paraBirimiListesiLocal[0] || 'TL');
  var [odemeTip, setOdemeTip] = useState(kayit?.odemeTipi || (odemeTipleri[0]?.ad || 'NAKİT'));
  var [banka, setBanka] = useState(kayit?.bankaAdi || '');
  var [aktifMi, setAktifMi] = useState(kayit?.aktifMi !== false);
  var [odemGunu, setOdemGunu] = useState(kayit?.odemGunu || 1);
  var [giderGrubuId, setGiderGrubuId] = useState(kayit?.giderGrubuId || giderGruplari[0]?.giderGrubuId || '');
  var [personelHedefId, setPersonelHedefId] = useState(kayit?.personelHedefId || personeller[0]?.personelId || '');

  // Ay bazlı ödeme günleri
  const AY_ISIMLERI = ['Oca','Şub','Mar','Nis','May','Haz','Tem','Ağu','Eyl','Eki','Kas','Ara'];
  var parsedGunler = {};
  try { if (kayit?.aylikOdemeGunleri) parsedGunler = JSON.parse(kayit.aylikOdemeGunleri); } catch {}
  var [aylikGunler, setAylikGunler] = useState(parsedGunler);
  var [aylikAktif, setAylikAktif] = useState(Object.keys(parsedGunler).length > 0);

  var toplam = (parseFloat(tutar) || 0) * (parseInt(miktar) || 1);
  var bankaGerekli = odemeTip !== 'NAKİT' && (bankalar.length > 0);

  return createPortal(
    <ModalShell
      icon="🔁"
      iconColor="from-rose-500 to-red-600"
      title={`Sabit Gider ${duzenleme ? 'Düzenle' : 'Ekle'}`}
      subtitle="Düzenli tekrarlanan gider şablonları"
      onClose={onKapat}
      size="md"
      zIndex="z-[9999]"
      footer={
        <>
          {duzenleme && (
            <button onClick={() => { if (window.confirm('Bu şablonu silmek istiyor musunuz?')) onSil(kayit.sabitGiderId); }}
              className="px-8 py-3.5 rounded-2xl border-2 border-rose-100 text-[10px] font-black text-rose-500 uppercase tracking-widest hover:bg-rose-50 transition-all active:scale-95">SİL</button>
          )}
          <button onClick={() => {
            if (!ad.trim()) return alert('Şablon adı zorunludur.');
            onKaydet({
              sabitGiderId: kayit?.sabitGiderId, ad, giderTipi: tip, aciklama,
              birimTutar: parseFloat(tutar) || 0, miktar: parseInt(miktar) || 1,
              paraBirimi, odemeTipi: odemeTip, bankaAdi: bankaGerekli ? banka : '',
              aktifMi, odemGunu: parseInt(odemGunu) || 1,
              aylikOdemeGunleri: aylikAktif && Object.keys(aylikGunler).length > 0 ? JSON.stringify(aylikGunler) : JSON.stringify(Object.fromEntries(Array.from({length:12}, (_, i) => [i+1, parseInt(odemGunu) || 1]))),
              giderGrubuId: tip === 'gider' ? (parseInt(giderGrubuId) || null) : null,
              personelHedefId: tip === 'personel' ? (parseInt(personelHedefId) || null) : null,
            });
          }} className="px-10 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-2xl text-[10px] font-black shadow-xl shadow-blue-200 transition-all hover:-translate-y-1 active:translate-y-0 tracking-widest uppercase">
            💾 KAYDET
          </button>
        </>
      }
    >
          <div className="flex gap-4 p-1.5 bg-gray-50 rounded-2xl border-2 border-gray-100">
            {[['gider', 'Gider'], ['personel', 'Personel Gider']].map(([v, l]) => (
              <button key={v} onClick={() => setTip(v)}
                className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${tip === v ? 'bg-white text-gray-800 shadow-sm border-2 border-gray-100' : 'text-gray-400 hover:text-gray-600'}`}>
                {l}
              </button>
            ))}
          </div>

          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1">ŞABLON ADI *</label>
            <input value={ad} onChange={e => setAd(e.target.value)} placeholder="Örn: Aylık Kira Ödemesi" className={INP()} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1">{tip === 'gider' ? 'GİDER GRUBU' : 'PERSONEL'}</label>
              <select value={tip === 'gider' ? giderGrubuId : personelHedefId}
                onChange={e => tip === 'gider' ? setGiderGrubuId(e.target.value) : setPersonelHedefId(e.target.value)}
                className={INP() + ' cursor-pointer'}>
                {tip === 'gider'
                  ? giderGruplari.map(g => <option key={g.giderGrubuId} value={g.giderGrubuId}>{g.ad}</option>)
                  : personeller.map(p => <option key={p.personelId} value={p.personelId}>{p.adSoyad}</option>)
                }
              </select>
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1">VARSAYILAN ÖDEME GÜNÜ (1-31)</label>
              <input type="number" min="1" max="31" value={odemGunu} onChange={e => setOdemGunu(e.target.value)} className={INP()} />
            </div>
          </div>

          {/* Ay bazlı esnek ödeme günü */}
          <div className="border-2 border-gray-100 rounded-2xl p-4">
            <label className="flex items-center gap-3 text-[10px] font-black text-gray-500 uppercase tracking-widest cursor-pointer mb-3">
              <input type="checkbox" checked={aylikAktif} onChange={e => setAylikAktif(e.target.checked)} className="w-4 h-4 rounded text-blue-500 border-gray-300" />
              Ay Bazlı Farklı Ödeme Günleri
            </label>
            {aylikAktif && (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {AY_ISIMLERI.map((ay, i) => {
                  const ayNo = String(i + 1);
                  return (
                    <div key={ayNo} className="flex flex-col items-center">
                      <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">{ay}</span>
                      <input type="number" min="1" max="31"
                        value={aylikGunler[ayNo] || ''}
                        placeholder={String(odemGunu)}
                        onChange={e => {
                          const v = parseInt(e.target.value);
                          setAylikGunler(p => {
                            const next = { ...p };
                            if (v > 0 && v <= 31) next[ayNo] = v;
                            else delete next[ayNo];
                            return next;
                          });
                        }}
                        className="w-full text-center border-2 border-gray-100 rounded-xl px-1 py-2 text-xs font-black text-gray-700 focus:border-blue-400 focus:outline-none transition-colors" />
                    </div>
                  );
                })}
              </div>
            )}
            {aylikAktif && <p className="text-[9px] font-bold text-gray-400 mt-2 text-center">Boş bırakılan aylar varsayılan günü kullanır</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1">BİRİM TUTAR</label>
              <div className="relative">
                <input type="number" value={tutar} onChange={e => setTutar(e.target.value)} className={INP() + ' pr-20'} />
                <select value={paraBirimi} onChange={e => setParaBirimi(e.target.value)} className="absolute right-2 top-2 bottom-2 rounded-xl border-none bg-gray-50 text-[10px] font-black px-2 focus:ring-0">
                  {(paraBirimleri.length ? paraBirimleri.map(p => p.tcmbKod || p.ad) : ['TL', 'USD', 'EUR']).map(k => <option key={k}>{k}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1">MİKTAR</label>
              <input type="number" min="1" value={miktar} onChange={e => setMiktar(e.target.value)} className={INP()} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1">ÖDEME TİPİ</label>
              <select value={odemeTip} onChange={e => setOdemeTip(e.target.value)} className={INP() + ' cursor-pointer'}>
                {(odemeTipleri.length ? odemeTipleri.map(o => o.ad) : ['NAKİT', 'BANKA HAVALE', 'KREDİ KARTI', 'ÇEK']).map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            {bankaGerekli && (
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1">BANKA</label>
                <select value={banka} onChange={e => setBanka(e.target.value)} className={INP() + ' cursor-pointer'}>
                  {(bankalar.length ? bankalar.map(b => b.ad) : ['QNB', 'Ziraat', 'Garanti', 'İş Bankası', 'Akbank']).map(b => <option key={b}>{b}</option>)}
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1">AÇIKLAMA</label>
            <textarea value={aciklama} onChange={e => setAciklama(e.target.value)} rows={2} className={INP() + ' resize-none'} />
          </div>

          <div className="bg-emerald-50 border-2 border-emerald-100 rounded-[2rem] p-5 flex justify-between items-center">
            <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">TOPLAM TUTAR</span>
            <span className="text-xl font-black text-emerald-700 tracking-tighter">{toplam.toFixed(2)} <span className="text-[10px]">{paraBirimi}</span></span>
          </div>

          <label className="flex items-center justify-between bg-gray-50/50 p-4 rounded-2xl border-2 border-gray-100 shadow-inner cursor-pointer">
            <span className={`text-[10px] font-black uppercase tracking-widest ${aktifMi ? 'text-blue-600' : 'text-gray-400'}`}>ŞABLON AKTİF</span>
            <div className="relative">
              <input type="checkbox" checked={aktifMi} onChange={e => setAktifMi(e.target.checked)} className="sr-only peer" />
              <div className="w-12 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-6"></div>
            </div>
          </label>
    </ModalShell>,
    document.body
  );
}

// ── Sabit Gider Kart (mobil) ──
function SabitGiderKart({ s, onDuzenle }) {
  return (
    <div className={`group relative bg-white border-2 border-gray-100 rounded-[2.5rem] p-6 transition-all duration-500 shadow-sm hover:shadow-xl hover:-translate-y-1 transform-gpu cursor-pointer text-left leading-none flex flex-col ${!s.aktifMi ? 'grayscale opacity-60' : ''}`} onClick={() => onDuzenle(s)}>
      <div className="flex items-start justify-between mb-5 gap-2">
        <div className="flex flex-col gap-2 min-w-0">
          <span className="text-[9px] font-black text-gray-300 uppercase tracking-widest opacity-80">GÜN: {s.odemGunu || 1}.</span>
          <span className="text-sm md:text-base font-black text-gray-800 tracking-tight leading-none group-hover:text-blue-600 transition-colors truncate whitespace-normal break-words">{s.ad}</span>
        </div>
        <span className="text-sm md:text-base font-black text-rose-600 whitespace-nowrap tracking-tighter">
          {(s.toplamTutar || 0).toLocaleString('tr-TR')} <span className="text-[10px] uppercase">{s.paraBirimi}</span>
        </span>
      </div>
      <div className="mt-auto pt-4 border-t border-gray-50 flex flex-wrap gap-2 text-[9px] font-black uppercase tracking-widest">
        <span className={`px-3 py-1.5 rounded-xl border ${s.giderTipi === 'personel' ? 'bg-purple-50 text-purple-600 border-purple-100' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
          {s.giderTipi === 'personel' ? 'Personel' : 'Gider'}
        </span>
        <span className="px-3 py-1.5 rounded-xl bg-blue-50 text-blue-600 border border-blue-100">{s.odemeTipi}</span>
      </div>
    </div>
  );
}

// ── Sabit Giderler Tab ──
function SabitGiderlerTab({ giderGruplari, personeller, odemeTipleri, bankalar, paraBirimleri, giderEkle }) {
  var [sabits, setSabits] = useState([]);
  var [yukleniyor, setYukleniyor] = useState(true);
  var [modal, setModal] = useState(null);
  var [uygulaniyor, setUygulaniyor] = useState(false);
  var [uygulamaAy, setUygulamaAy] = useState(() => {
    var now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  var fetchSabits = useCallback(async () => {
    setYukleniyor(true);
    try {
      var data = await apiRequest('sabit-gider');
      setSabits(data || []);
    } catch { /* sessiz */ }
    setYukleniyor(false);
  }, []);

  useEffect(() => { fetchSabits(); }, [fetchSabits]);

  async function handleKaydet(data) {
    try {
      if (data.sabitGiderId) await apiRequest('sabit-gider', 'PUT', data);
      else await apiRequest('sabit-gider', 'POST', data);
      await fetchSabits(); setModal(null);
    } catch (e) { alert(e?.message || 'Kaydedilemedi.'); }
  }

  async function handleSil(id) {
    try {
      await apiRequest(`sabit-gider/${id}`, 'DELETE');
      await fetchSabits(); setModal(null);
    } catch (e) { alert(e?.message || 'Silinemedi.'); }
  }

  async function handleUygula() {
    var aktifler = sabits.filter(s => s.aktifMi);
    if (!aktifler.length) return alert('Aktif şablon bulunmuyor.');
    var [yil, ay] = uygulamaAy.split('-').map(Number);
    if (!window.confirm(`${aktifler.length} adet aktif şablon gider kaydına dönüştürülecek. Onaylıyor musunuz?`)) return;

    setUygulaniyor(true);
    var hatalar = 0;
    for (var s of aktifler) {
      try {
        var gun = s.odemGunu || 1;
        var tarih = `${String(Math.min(gun, new Date(yil, ay, 0).getDate())).padStart(2, '0')}.${String(ay).padStart(2, '0')}.${yil}`;
        await giderEkle({ altTip: s.giderTipi, ad: s.aciklama || s.ad, tutar: s.birimTutar, miktar: s.miktar, paraBirimi: s.paraBirimi, odemeTip: s.odemeTipi, banka: s.bankaAdi || '', giderGrubuId: s.giderGrubuId || null, personelHedefId: s.personelHedefId || null, tarih, saat: '00:00' });
      } catch { hatalar++; }
    }
    setUygulaniyor(false);
    alert(hatalar > 0 ? `${aktifler.length - hatalar} kayıt oluşturuldu, ${hatalar} hata.` : 'Tüm kayıtlar başarıyla oluşturuldu.');
  }

  var aktifSayi = sabits.filter(s => s.aktifMi).length;
  var toplamTutar = sabits.filter(s => s.aktifMi).reduce((acc, s) => acc + (s.toplamTutar || 0), 0);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-[2.5rem] p-8 text-white relative overflow-hidden shadow-xl">
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="relative flex flex-col md:flex-row md:items-center gap-8 text-left leading-none">
          <div className="flex-1">
            <h2 className="text-xl md:text-2xl font-black uppercase tracking-tighter mb-2">Şablonları Uygula</h2>
            <p className="text-xs font-bold opacity-70 uppercase tracking-widest leading-relaxed">Seçili ay için aktif olan tüm şablonları gider olarak kaydet</p>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <button onClick={handleUygula} disabled={uygulaniyor || !aktifSayi}
              className="px-10 py-4 bg-white text-[10px] font-black uppercase tracking-widest text-blue-600 rounded-2xl shadow-xl hover:-translate-y-1 active:translate-y-0 transition-all disabled:opacity-50">
              {uygulaniyor ? '⏳ UYGULANIYOR...' : `▶ UYGULA (${aktifSayi} ŞABLON · ${toplamTutar.toLocaleString('tr-TR')} TL)`}
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between px-4 text-left leading-none">
        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">SABİT GİDER ŞABLONLARI ({sabits.length})</h3>
        <button onClick={() => setModal('ekle')} className="px-6 py-3 bg-blue-50 text-blue-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-100 transition-all active:scale-95">＋ YENİ ŞABLON</button>
      </div>

      <div className="block lg:hidden space-y-4">
        {yukleniyor ? <LoadingSpinner fullPage text="Yükleniyor..." /> :
          !sabits.length ? <EmptyState text="Şablon bulunmuyor." /> :
            sabits.map(s => <SabitGiderKart key={s.sabitGiderId} s={s} onDuzenle={setModal} />)}
      </div>

      <div className="hidden lg:block bg-white border-2 border-gray-100 rounded-[3rem] shadow-sm overflow-hidden">
        <DataTable
          anahtarAlan="sabitGiderId"
          satirlar={yukleniyor ? [] : sabits}
          bosMetin={yukleniyor ? 'Veriler hazırlanıyor…' : 'Şablon kaydı bulunamadı'}
          satirSinifi={s => !s.aktifMi ? 'grayscale opacity-50' : ''}
          kolonlar={[
            {
              anahtar: 'ad', baslik: 'Şablon / Açıklama', siralanir: true, filtrelenir: true,
              ciz: s => (
                <>
                  <div className="font-black text-gray-800 uppercase tracking-tight mb-1.5">{s.ad}</div>
                  {s.aciklama && <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest opacity-80">{s.aciklama}</div>}
                </>
              ),
            },
            {
              anahtar: 'giderTipi', baslik: 'Tip / Grup', siralanir: true, filtrelenir: true,
              ciz: s => (
                <>
                  <Rozet renk={s.giderTipi === 'personel' ? 'violet' : 'gray'}>
                    {s.giderTipi === 'personel' ? 'Personel' : 'Gider'}
                  </Rozet>
                  <div className="mt-2 text-gray-400 font-black text-[9px] uppercase tracking-widest">
                    {s.giderGrubuAd || s.personelHedefAd || '—'}
                  </div>
                </>
              ),
            },
            {
              anahtar: 'odemGunu', baslik: 'Ödeme günü', hizala: 'orta', siralanir: true,
              ciz: s => <span className="font-black text-blue-600">{s.odemGunu || 1}. GÜN</span>,
            },
            {
              anahtar: 'toplamTutar', baslik: 'Tutar', hizala: 'sag', siralanir: true,
              ciz: s => (
                <span className="font-black text-rose-600 text-sm tracking-tighter whitespace-nowrap">
                  {(s.toplamTutar || 0).toLocaleString('tr-TR')} {s.paraBirimi}
                </span>
              ),
            },
            {
              anahtar: 'aktifMi', baslik: 'Durum', hizala: 'orta', siralanir: true,
              ciz: s => <Rozet renk={s.aktifMi ? 'emerald' : 'gray'}>{s.aktifMi ? 'Aktif' : 'Pasif'}</Rozet>,
            },
            {
              anahtar: 'islem', baslik: 'İşlem', hizala: 'sag', genislik: 'w-24',
              ciz: s => <EditButton onClick={() => setModal(s)} />,
            },
          ]}
        />
      </div>

      {modal && <SabitGiderModal key={modal === 'ekle' ? 'ekle' : modal.sabitGiderId} kayit={modal === 'ekle' ? null : modal} giderGruplari={giderGruplari} personeller={personeller} odemeTipleri={odemeTipleri} bankalar={bankalar} paraBirimleri={paraBirimleri} onKaydet={handleKaydet} onSil={handleSil} onKapat={() => setModal(null)} />}
    </div>
  );
}

// ── Mobil Gider Kart ──
function GiderKart({ g, onClick }) {
  return (
    <div className="group relative bg-white border-2 border-gray-100 rounded-[2.5rem] p-6 transition-all duration-500 shadow-sm hover:shadow-xl hover:border-rose-100 hover:-translate-y-1 transform-gpu cursor-pointer text-left leading-none flex flex-col" onClick={onClick}>
      <div className="flex items-start justify-between mb-5 gap-2">
        <div className="flex flex-col gap-2 min-w-0">
          <span className="text-[9px] font-black text-gray-300 uppercase tracking-widest opacity-80 font-mono">{g.tarih} {g.saat}</span>
          <span className="text-sm md:text-base font-black text-gray-800 tracking-tight leading-none group-hover:text-rose-600 transition-colors truncate whitespace-normal break-words">{g.ad || g.grup || '—'}</span>
        </div>
        <span className="text-sm md:text-base font-black text-rose-600 whitespace-nowrap tracking-tighter">
          {(g.toplam || g.tutar || 0).toFixed(2)} <span className="text-[10px] uppercase">{g.paraBirimi}</span>
        </span>
      </div>
      <div className="mt-auto pt-4 border-t border-gray-50 flex flex-wrap gap-2 text-[9px] font-black uppercase tracking-widest">
        <span className="px-3 py-1.5 rounded-xl bg-gray-100 text-gray-500 border border-gray-200">{g.altTip === 'personel' ? 'Personel Gider' : 'Gider'}</span>
        <span className="px-3 py-1.5 rounded-xl bg-blue-50 text-blue-600 border border-blue-100">{g.odemeTip}</span>
        {g.personel && <span className="px-3 py-1.5 rounded-xl bg-purple-50 text-purple-600 border border-purple-100">{g.personel}</span>}
      </div>
    </div>
  );
}

// ── Ana Sayfa ──
export default function GiderListesi() {
  var bugun = bugunStr();
  var ayBasi = ayinBasiStr();

  var [baslar, setBaslar] = useState(ayBasi);
  var [bitis, setBitis] = useState(bugun);
  var [giderTip, setGiderTip] = useState('Hepsi');
  var [odemeTip, setOdemeTip] = useState('Hepsi');
  var [bankaFiltre, setBankaFiltre] = useState('Hepsi');
  var [adAciklama, setAdAciklama] = useState('');
  var [filtreKey, setFiltreKey] = useState({ baslar: ayBasi, bitis: bugun, giderTip: 'Hepsi', odemeTip: 'Hepsi', banka: 'Hepsi', ad: '' });
  var [aktifTab, setAktifTab] = useState('liste');
  var [aktifSayfa, setAktifSayfa] = useState(1);
  var [sayfaBasi, setSayfaBasi] = useState(100);
  var { giderler: ctxGiderler, giderEkle: ctxGiderEkle, giderGuncelle, giderSil, giderGruplari, gelirGruplari, gelirEkle, personeller, odemeTipleri, bankalar, paraBirimleri, dovizKurlari, fetchVeriler } = useKasa();
  var [modal, setModal] = useState(null);
  var [excelModal, setExcelModal] = useState(false);
  var [silOnay, setSilOnay] = useState(null);

  useEffect(function () { fetchVeriler(); }, []); // eslint-disable-line

  function filtrele() { setAktifSayfa(1); setFiltreKey({ baslar, bitis, giderTip, odemeTip, banka: bankaFiltre, ad: adAciklama }); }
  function temizle() {
    setAktifSayfa(1);
    setBaslar(ayBasi); setBitis(bugun);
    setGiderTip('Hepsi'); setOdemeTip('Hepsi'); setBankaFiltre('Hepsi'); setAdAciklama('');
    setFiltreKey({ baslar: ayBasi, bitis: bugun, giderTip: 'Hepsi', odemeTip: 'Hepsi', banka: 'Hepsi', ad: '' });
  }

  var liste = ctxGiderler.filter(function (g) {
    var gTarih = ddmmyyyyToDate(g.tarih);
    var fBas = ddmmyyyyToDate(filtreKey.baslar); if (fBas) fBas.setHours(0, 0, 0, 0);
    var fBit = ddmmyyyyToDate(filtreKey.bitis); if (fBit) fBit.setHours(23, 59, 59, 999);
    if (gTarih) gTarih.setHours(0, 0, 0, 0);
    if (fBas && gTarih && gTarih < fBas) return false;
    if (fBit && gTarih && gTarih > fBit) return false;
    if (filtreKey.giderTip !== 'Hepsi') { if ((g.altTip || 'gider') !== (filtreKey.giderTip === 'Personel Gider' ? 'personel' : 'gider')) return false; }
    if (filtreKey.odemeTip !== 'Hepsi' && g.odemeTip !== filtreKey.odemeTip) return false;
    if (filtreKey.banka && filtreKey.banka !== 'Hepsi' && (g.banka || '') !== filtreKey.banka) return false;
    if (filtreKey.ad && !(g.ad || '').toLocaleLowerCase('tr-TR').includes(filtreKey.ad.toLocaleLowerCase('tr-TR'))) return false;
    return true;
  // Kolon filtresi ve sıralama DataTable içinde; burada yalnızca üst filtre
  // uygulanır ve en yeni kayıt üste alınır.
  }).sort(function (a, b) {
    return String(b.tarih || '').localeCompare(String(a.tarih || ''), 'tr', { numeric: true });
  });

  var listePage = liste.slice((aktifSayfa - 1) * sayfaBasi, aktifSayfa * sayfaBasi);

  var ozet = {};
  liste.forEach(function (g) {
    var pk = (g.odemeTip || 'DİĞER').toUpperCase('tr'); 
    ozet[pk] = (ozet[pk] || 0) + (g.toplam || g.tutar || 0) * (g.kurDegeri || 1); 
  });
  var genelToplam = liste.reduce(function (a, g) { return a + (g.toplam || g.tutar || 0) * (g.kurDegeri || 1); }, 0);

  async function handleKaydet(data) { if (data.kasaGiderId || data.id) await giderGuncelle(data); else await ctxGiderEkle(data); }

  function handleSatirSil(g) { setSilOnay(g); }
  async function handleSilOnayla() {
    if (!silOnay) return;
    await giderSil(silOnay.id);
    setSilOnay(null);
  }

  var stats = [
    { label: 'TOPLAM GİDER', value: formatPara(genelToplam, 'TL'), icon: '📉', color: 'from-rose-500 to-red-600 shadow-rose-100' },
    { label: 'KAYIT SAYISI', value: liste.length, icon: '📋', color: 'from-gray-700 to-gray-900 shadow-gray-200' },
    { label: 'NAKİT ÇIKIŞ', value: formatPara(ozet['NAKİT'] || 0, 'TL'), icon: '💵', color: 'from-amber-400 to-orange-500 shadow-amber-100' },
    { label: 'KART / HAVALE', value: formatPara((ozet['KREDİ KARTI'] || 0) + (ozet['BANKA HAVALE'] || 0), 'TL'), icon: '💳', color: 'from-blue-500 to-indigo-600 shadow-blue-100' }
  ];

  return (
    <div className="p-3 md:p-6 space-y-7 min-h-screen bg-gray-50/50 animate-fadeIn leading-none" style={{ transform: 'translateZ(0)' }}>
      <PageHeader
        icon="🛒"
        iconColor="from-rose-500 to-red-700"
        iconShadow="shadow-rose-200"
        title="Gider Listesi"
        subtitle="Harcama takibi ve personel giderleri"
      >
        <div className="flex flex-wrap gap-3">
          <div className="flex p-1 bg-gray-100 rounded-2xl mr-2">
            {[['liste', '📋 LİSTE'], ['sabit', '🔁 SABİT']].map(([k, l]) => (
              <button key={k} onClick={() => setAktifTab(k)}
                className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${aktifTab === k ? 'bg-white text-gray-800 shadow-sm border border-gray-200' : 'text-gray-400'}`}>
                {l}
              </button>
            ))}
          </div>
          {aktifTab === 'liste' && (
            <>
              <button onClick={() => downloadExcel('gider-listesi', 'Giderler', ['Tarih','Saat','Açıklama','Tip','Ödeme Tipi','Personel','Toplam','Para Birimi'], liste.map(g => [g.tarih, g.saat||'', g.ad||'', g.altTip==='personel'?'Personel Gideri':(g.grup||'Genel Gider'), g.odemeTip||'—', g.personel||'—', (g.toplam||(g.tutar*(g.miktar||1))||0).toFixed(2), g.paraBirimi||'TL']))} className="px-6 py-4 rounded-2xl border-2 border-rose-100 text-[10px] font-black text-rose-700 uppercase tracking-widest hover:bg-rose-50 bg-white transition-all active:scale-95 shadow-sm">📊 DIŞA AKTAR</button>
              <button onClick={() => setExcelModal(true)} className="px-6 py-4 rounded-2xl border-2 border-gray-100 text-[10px] font-black text-gray-700 uppercase tracking-widest hover:bg-gray-50 bg-white transition-all active:scale-95 shadow-sm">📥 EXCEL İÇE AKTAR</button>
              <button onClick={() => setModal('ekle')} className="group flex items-center gap-3 bg-gradient-to-r from-rose-600 to-red-700 text-white px-8 py-4 rounded-2xl text-[10px] font-black shadow-xl shadow-rose-200 transition-all hover:-translate-y-1 active:translate-y-0 uppercase tracking-widest relative overflow-hidden leading-none">
                <span className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></span>
                <span className="relative text-xl transition-transform group-hover:rotate-90">＋</span>
                <span className="relative">YENİ GİDER EKLE</span>
              </button>
            </>
          )}
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 leading-none">
        {stats.map((k, i) => (
          <div key={i} className={`relative overflow-hidden bg-gradient-to-br ${k.color} rounded-[2rem] p-5 text-white shadow-xl hover:shadow-2xl transition-all duration-300 group`}>
            <div className="absolute -right-4 -bottom-4 text-6xl opacity-12 pointer-events-none group-hover:scale-125 transition-transform duration-500 transform-gpu">{k.icon}</div>
            <p className="text-[9px] font-black uppercase tracking-widest opacity-70 mb-1 leading-none text-left">{k.label}</p>
            <p className="text-xl md:text-2xl font-black tracking-tight leading-none text-left">{k.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-[3rem] border border-gray-200 shadow-sm overflow-hidden min-h-[500px]">
        {aktifTab === 'sabit' ? (
          <div className="p-6 md:p-8 animate-fadeIn"><SabitGiderlerTab giderGruplari={giderGruplari} personeller={personeller} odemeTipleri={odemeTipleri} bankalar={bankalar} paraBirimleri={paraBirimleri} giderEkle={ctxGiderEkle} /></div>
        ) : (
          <>
            <div className="px-6 md:px-8 py-6 border-b border-gray-100 bg-gray-50/30 flex flex-col xl:flex-row gap-6">
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 w-full text-left">
                <div className="relative">
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-4 bg-white/0 px-1 z-10">BAŞLANGIÇ</label>
                  <DatePicker value={baslar} onChange={setBaslar} />
                </div>
                <div className="relative">
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-4 bg-white/0 px-1 z-10">BİTİŞ</label>
                  <DatePicker value={bitis} onChange={setBitis} />
                </div>
                <div className="relative">
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-4 bg-white/0 px-1 z-10">TİP</label>
                  <SearchableSelect value={giderTip} onChange={setGiderTip} options={GIDER_TIPLERI} />
                </div>
                <div className="relative">
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-4 bg-white/0 px-1 z-10">ÖDEME</label>
                  <SearchableSelect value={odemeTip} onChange={setOdemeTip} options={['Hepsi', ...(odemeTipleri.length ? odemeTipleri.map(o => o.ad) : ODEME_TIPLER.slice(1))]} />
                </div>
                <div className="relative">
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-4 bg-white/0 px-1 z-10">BANKA</label>
                  <SearchableSelect value={bankaFiltre} onChange={setBankaFiltre} options={['Hepsi', ...(bankalar || []).map(b => b.ad || b)]} />
                </div>
                <div className="relative">
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-4 bg-white/0 px-1 z-10">ARAMA</label>
                  <input value={adAciklama} onChange={e => setAdAciklama(e.target.value)} placeholder="Açıklama..." className="w-full border-2 border-gray-100 rounded-2xl px-5 py-3.5 text-[10px] font-black tracking-widest uppercase bg-white focus:outline-none focus:border-blue-400 shadow-sm" />
                </div>
              </div>
              <div className="flex items-center justify-between xl:justify-end gap-3 w-full xl:w-auto ml-auto">
                <button onClick={temizle} className="px-6 py-4 text-[10px] font-black text-rose-500 uppercase tracking-widest hover:bg-rose-50 rounded-2xl transition-all active:scale-95 shadow-sm border-2 border-rose-50">SIFIRLA</button>
                <button onClick={filtrele} className="px-8 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-2xl text-[10px] font-black shadow-xl shadow-blue-200 transition-all uppercase tracking-widest active:scale-95 transform-gpu">FİLTRELE</button>
              </div>
            </div>

            <div className="p-6 md:p-8">
              <div className="block lg:hidden space-y-4">
                {!liste.length ? <EmptyState text="Kayıt bulunamadı." /> : listePage.map(g => <GiderKart key={g.id} g={g} onClick={() => setModal(g)} />)}
              </div>
              <div className="hidden lg:block overflow-x-auto">
                <DataTable
                  anahtarAlan="id"
                  satirlar={listePage}
                  bosMetin="Harcama kaydı bulunamadı"
                  yogun
                  kolonlar={[
                    {
                      anahtar: 'tarih', baslik: 'Tarih / Saat', siralanir: true, filtrelenir: true,
                      deger: g => (g.tarih || '') + ' ' + (g.saat || ''),
                      ciz: g => <span className="whitespace-nowrap opacity-70 font-mono tracking-tighter">{(g.tarih || '') + ' ' + (g.saat || '')}</span>,
                    },
                    {
                      anahtar: 'ad', baslik: 'Gider adı', siralanir: true, filtrelenir: true,
                      deger: g => g.ad || g.grup,
                      ciz: g => <span className="uppercase tracking-tight font-black">{g.ad || g.grup}</span>,
                    },
                    {
                      anahtar: 'odemeTip', baslik: 'Ödeme tipi', siralanir: true, filtrelenir: true,
                      ciz: g => <span className="opacity-80 uppercase text-[10px] font-black tracking-widest">{g.odemeTip}</span>,
                    },
                    {
                      anahtar: 'toplam', baslik: 'Tutar / Toplam', hizala: 'sag', siralanir: true, filtrelenir: true,
                      ciz: g => (
                        <div className="whitespace-nowrap">
                          <span className="font-black text-rose-600 text-sm tracking-tighter">
                            {formatPara(g.toplam || g.tutar || 0)} <span className="text-[9px] opacity-70">{g.paraBirimi}</span>
                          </span>
                          {g.paraBirimi !== 'TL' && g.paraBirimi !== 'TRY' && (g.kurDegeri || 0) > 0 && (
                            <div className="text-[9px] text-blue-500 font-black opacity-80 tracking-widest mt-1">
                              {formatPara((g.toplam || g.tutar || 0) * (g.kurDegeri || 1))} TL
                            </div>
                          )}
                        </div>
                      ),
                    },
                    {
                      anahtar: 'personel', baslik: 'Personel / Grup', siralanir: true, filtrelenir: true,
                      deger: g => g.personel || g.grup || '',
                      ciz: g => (
                        <div className="uppercase text-[10px] font-black tracking-widest opacity-80">
                          <span className="text-gray-400 block mb-1 text-[8px]">{g.altTip === 'personel' ? 'PERSONEL' : 'GİDER / GRUP'}</span>
                          {g.personel || g.grup || '—'}
                        </div>
                      ),
                    },
                    {
                      anahtar: 'islem', baslik: 'İşlem', hizala: 'sag', genislik: 'w-24',
                      ciz: g => <ActionButtons onEdit={() => setModal(g)} onDelete={() => handleSatirSil(g)} />,
                    },
                  ]}
                />
              </div>
              <Pagination toplam={liste.length} sayfaBasi={sayfaBasi} sayfaBasiDegistir={setSayfaBasi} aktifSayfa={aktifSayfa} sayfaDegistir={setAktifSayfa} />
            </div>
          </>
        )}
      </div>

      {excelModal && (
        <ExcelImportModal onClose={() => setExcelModal(false)} gelirEkle={gelirEkle} giderEkle={ctxGiderEkle} gelirGruplari={gelirGruplari || []} giderGruplari={giderGruplari || []} odemeTipleri={odemeTipleri || []} bankalar={bankalar || []} />
      )}

      {modal && (
        <GiderEkleModal mevcutKayit={modal === 'ekle' ? null : modal} onClose={() => setModal(null)} onKaydet={handleKaydet} onSil={giderSil} giderGruplari={giderGruplari || []} personeller={personeller || []} odemeTipleri={(odemeTipleri || []).map(o => o.ad || o)} bankalar={(bankalar || []).map(b => b.ad || b)} paraBirimleri={(paraBirimleri || []).map(p => p.tcmbKod || p.ad || p)} dovizKurlari={dovizKurlari || {}} />
      )}

      {/* Silme Onay Popup */}
      <OnayPopup
        acik={!!silOnay}
        baslik="Gider Kaydını Sil"
        mesaj={silOnay ? `"${silOnay.ad || 'Bu kayıt'}" silinecek. Bu işlem geri alınamaz. Devam etmek istiyor musunuz?` : ''}
        onEvet={handleSilOnayla}
        onHayir={() => setSilOnay(null)}
      />

      <UsageInfo
        color="rose"
        description="Gider Listesi, işletme harcamalarını, personel giderlerini ve sabit maliyetlerinizi organize etmenize yardımcı olur."
        scenarios={[
          { title: "Harcama Denetimi", content: "Elektrik, su, mutfak alışverişi gibi kalemleri gruplandırarak giderlerinizi kontrol altında tutabilirsiniz." },
          { title: "Sabit Giderler", content: "Kira, aidat gibi her ay tekrarlanan ödemeleri 'Sabit' sekmesinden şablonlaştırarak tek tıkla otomatik olarak oluşturabilirsiniz." }
        ]}
      />

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-scaleIn { animation: scaleIn 0.3s ease-out forwards; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
